// src/modules/socialhive/mobile/MobileLoginEvent.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './MobileLoginEvent.css';
import MobileEventsHome from './MobileEventsHome';
import {
  TbCalendarEvent,
  TbMapPin,
  TbUsers,
  TbLogout,
  TbWorld,
  TbMail,
  TbChevronDown,
  TbCircleCheck,
  TbShare,
  TbHome,
  TbBell,
  TbTicket,
  TbBrandInstagram,
  TbBrandX,
  TbBrandLinkedin,
  TbBrandWhatsapp,
  TbBrandTelegram,
  TbLink,
  TbArrowLeft,
  TbChevronRight
} from 'react-icons/tb';
import { useLanguage } from '../../LanguageProvider';
import Services from '../../services/Services';
import { broadcastAuthMessage } from '../../utils/authChannel';
import MobileTicketsPage from './MobileTicketsPage';
import MobilePaymentPage from './MobilePaymentPage';
import { GeneralContext } from '../../GeneralProvider';
import MobileEventsNotification from './MobileEventsNotification';
import MobileChannelDetails from './MobileChannelDetails';
import MobileMyActivitiesPage from './MobileMyActivitiesPage';
import logo from '../../assets/logo.png'

function usePreventBodyScroll(active) {
  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);
}

/** Mobil alt sayfa (bottom-sheet) modal */
function MleSheet({ isOpen, onClose, title, children }) {
  usePreventBodyScroll(isOpen);
  if (!isOpen) return null;
  return (
    <div className="mle-sheet-backdrop" onClick={onClose}>
      <div
        className="mle-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mle-sheet-header" aria-live="polite">
          <div className="mle-sheet-handle" />
          <div className="mle-sheet-title">{title}</div>
          <button className="mle-sheet-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="mle-sheet-body">
          {children}
        </div>
        <div className="mle-sheet-safearea" />
      </div>
    </div>
  );
}

export default function MobileLoginEvent({
  event,
  onRegister,
  onReserve,
  mode, setMode
}) {
  const { getTranslation, language, changeLanguage } = useLanguage();
  const { myChannels, handleJoinChannel, handleChannelChange } = useContext(GeneralContext) || {};
  const toId = (v) => (v == null ? null : String(v));
  const reserveFlowRef = useRef({ started: false, targetChannelID: null, switchedOnce: false });
  const [showFullDesc, setShowFullDesc] = useState(false);

  const contentRef = useRef(null);
  const ctaRef = useRef(null);
  const prevAtBottomRef = useRef(false);
  const pageRef = useRef(null);

  // ---- Auth state
  const [apiToken, setApiToken] = useState('');
  const [isAuth, setIsAuth] = useState(false);
  const [authUser, setAuthUser] = useState(null);

  // ---- Bottom-sheets
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);

  // Yeni: dil seçici sheet
  const [langSheetOpen, setLangSheetOpen] = useState(false);
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [showHome, setShowHome] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [showChannelDetails, setShowChannelDetails] = useState(false);
  const [showMyActivities, setShowMyActivities] = useState(false);
  const [showSponsorTickets, setShowSponsorTickets] = useState(false);
  const [paymentOrigin, setPaymentOrigin] = useState('tickets');
  // ---- Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginRemember, setLoginRemember] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginIntent, setLoginIntent] = useState(null);

  // ---- Register form
  const [regName, setRegName] = useState('');
  const [regLastname, setRegLastname] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [paymentData, setPaymentData] = useState(null);

  const [successOpen, setSuccessOpen] = useState(false);
  const [successSummary, setSuccessSummary] = useState({ total: 0, count: 0 });
  const [participantCount, setParticipantCount] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  // Şifre kuralları
  const reqLen = regPassword.length >= 8;
  const reqSpecial = /[^A-Za-z0-9ÇÖÜĞŞİçöüğşı]/.test(regPassword);
  const reqNumber = /\d/.test(regPassword);
  const reqUpper = /[A-ZÇĞİÖŞÜ]/.test(regPassword);
  const reqLower = /[a-zçğıöşü]/.test(regPassword);

  // --- helpers
  const getSessionToken = () => {
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const localToken = localStorage.getItem('token');
    const sessionToken = sessionStorage.getItem('token');
    return (rememberMe && localToken) ? localToken : (sessionToken || null);
  };
  const getCurrentUserId = () => {
    try {
      const u = JSON.parse(sessionStorage.getItem('user') || localStorage.getItem('user') || 'null');
      return u?.userID || u?.userId || u?.id || null;
    } catch {
      return null;
    }
  };

  // Sayfanın en üst seviyede (component) içinde:
  useEffect(() => {
    const prev = document.body.style.overflow;
    // açılışta olası kilidi kaldır
    document.body.style.overflow = '';
    return () => {
      // bu sayfa çıkarken önceki değeri geri ver
      document.body.style.overflow = prev;
    };
  }, []);

  // MobileLoginEvent.jsx içinde (export default function MobileLoginEvent(...) üstlerine yakın bir yerde)
  useEffect(() => {
    // iOS + visualViewport kontrolü
    const isiOSDevice = (() => {
      const ua = navigator.userAgent || navigator.vendor || window.opera || "";
      const platform = navigator.platform || "";
      const iDevice = /iPad|iPhone|iPod/.test(ua);
      const iPadOS13plus = /MacIntel/.test(platform) && (navigator.maxTouchPoints || 0) > 1;
      return iDevice || iPadOS13plus;
    })();

    if (!isiOSDevice || !window.visualViewport) return;

    const setVars = () => {
      const vv = window.visualViewport;
      const vvh = Math.round(vv.height);
      const kb = Math.max(0, window.innerHeight - vvh); // klavye yüksekliği approx
      document.documentElement.style.setProperty('--vvh', `${vvh}px`);
      document.documentElement.style.setProperty('--keyboard-offset', `${kb}px`);
    };

    setVars();
    document.documentElement.classList.toggle('is-ios', isiOSDevice);
    window.visualViewport.addEventListener('resize', setVars);
    window.visualViewport.addEventListener('scroll', setVars);

    // input odaklanınca ortala (Safari bazen çok yukarı sürüklüyor)
    const onFocus = (e) => {
      const el = e.target;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) {
        setTimeout(() => {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 50);
      }
    };
    document.addEventListener('focusin', onFocus, { passive: true });

    return () => {
      window.visualViewport.removeEventListener('resize', setVars);
      window.visualViewport.removeEventListener('scroll', setVars);
      document.removeEventListener('focusin', onFocus);
    };
  }, []);

  const isAtContainerBottom = (el) => {
    if (!el) return false;
    const { scrollTop, clientHeight, scrollHeight } = el;
    const viewportBottom = scrollTop + clientHeight;
    const THRESHOLD = 2;
    const atBottom = viewportBottom >= (scrollHeight - THRESHOLD);
    prevAtBottomRef.current = atBottom;
    return atBottom;
  };

  const handleScrollOrResize = () => {
    requestAnimationFrame(() => {
      const scroller = pageRef.current;
      const cta = ctaRef.current;
      if (!scroller || !cta) return;
      const atBottom = isAtContainerBottom(scroller);
      if (atBottom) cta.classList.add('stuck');
      else cta.classList.remove('stuck');
    });
  };

  useEffect(() => {
    prevAtBottomRef.current = false;
    const scroller = pageRef.current;
    if (scroller) scroller.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize);
    handleScrollOrResize();
    return () => {
      if (scroller) scroller.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, []);

  // ----- Tarih/saat
  const eventStart = new Date(parseInt(event.eventStartDateTime, 10) * 1000);
  const eventEnd = new Date(parseInt(event.eventEndDateTime, 10) * 1000);

  const MONTH_KEYS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
  const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  const formatTime24 = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const weekdayKey = WEEKDAY_KEYS[eventStart.getDay()];
  const monthKey = MONTH_KEYS[eventStart.getMonth()];
  const dayOfMonth = eventStart.getDate();

  const dateText = `${getTranslation(weekdayKey)}, ${dayOfMonth} ${getTranslation(monthKey)}`;
  const timeText = `${formatTime24(eventStart)} - ${formatTime24(eventEnd)}`;

  // Konum
  thePlace: {
    /* no-op label block to keep diff tiny */
  }
  const placeTitle = event.isOnline === "1" ? "Online" : event.locationAddress;
  const locationText = event.isOnline === "1" ? "Etkinlik online olarak gerçekleştirilecektir." : "";

  useEffect(() => {
    let mounted = true;
    // session/local stratejine göre token
    if (!apiToken || !event?.eventID) {
      if (mounted) setParticipantCount(null);
      return () => { mounted = false; };
    }
    Services.SocialHiveGetEventSoldTickets(apiToken, event.eventID)
      .then((res) => {
        if (!mounted) return;
        const list = Array.isArray(res?.data?.result) ? res.data.result : [];
        setParticipantCount(list.length > 0 ? list.length : null);
      })
      .catch(() => {
        if (mounted) setParticipantCount(null);
      });
    return () => { mounted = false; };
    // isAuth/mode değiştiğinde (örn. başarılı satın alma sonrası) yeniden çek
  }, [event?.eventID, isAuth, mode, apiToken]);

  useEffect(() => {
    let mounted = true;

    if (!apiToken || !event?.eventID || !isAuth || !authUser?.id) {
      if (mounted) setMyTickets([]);
      return () => { mounted = false; };
    }

    Services.SocialHiveGetEventMyTickets(getSessionToken(), event.eventID, authUser.id)
      .then((res) => {
        if (!mounted) return;
        if (res?.data?.status) {
          const list = Array.isArray(res.data.result) ? res.data.result : [];
          setMyTickets(list);
        } else {
          setMyTickets([]);
        }
      })
      .catch(() => {
        if (mounted) setMyTickets([]);
      });

    return () => {
      mounted = false;
    };
  }, [apiToken, event?.eventID, isAuth, authUser?.id, mode]);

  const userHasTicket = Array.isArray(myTickets) && myTickets.length > 0;

  const renderFooter = () => {
    const hasAnyToken =
      !!(localStorage.getItem("token") || sessionStorage.getItem("token"));

    if (
      (userHasTicket ||
        showHome ||
        showNotifications ||
        showChannelDetails ||
        showMyActivities) &&
      hasAnyToken &&
      mode !== "payment" &&
      mode !== "tickets"
    ) {
      return (
        <nav className="mle-footer-nav" ref={ctaRef}>
          {/* HOME TAB */}
          <button
            type="button"
            className={
              'mle-footer-item' +
              (activeTab === 'home' ? ' is-active' : '')
            }
            onClick={() => {
              setMode("details")
              setShowHome(true);
              setShowNotifications(false);
              setShowSponsorTickets(false);
              setShowChannelDetails(false)
              setShowMyActivities(false);
              setActiveTab('home');
            }}
          >
            <TbHome className="mle-footer-ico" />
            <span className="mle-footer-label">
              {getTranslation('home') || 'Ev'}
            </span>
          </button>

          {/* NOTIFICATIONS TAB */}
          <button
            type="button"
            className={
              'mle-footer-item' +
              (activeTab === 'notifications' ? ' is-active' : '')
            }
            onClick={() => {
              setMode("details")
              setShowHome(false);
              setShowChannelDetails(false)
              setShowMyActivities(false);
              setShowNotifications(true);
              setActiveTab('notifications');
            }}
          >
            <TbBell className="mle-footer-ico" />
            <span className="mle-footer-label">
              {getTranslation('notifications') || 'Bildirim'}
            </span>
          </button>

          {/* MY ACTIVITIES TAB */}
          <button
            type="button"
            className={
              'mle-footer-item' +
              (activeTab === 'activities' ? ' is-active' : '')
            }
            onClick={() => {
              setMode("details");
              setShowHome(false);
              setShowNotifications(false);
              setShowSponsorTickets(false);
              setShowChannelDetails(false);
              setShowMyActivities(true);
              setActiveTab('activities');
            }}
          >
            <TbTicket className="mle-footer-ico" />
            <span className="mle-footer-label">
              {getTranslation('myActivities') || 'Aktiviteler'}
            </span>
          </button>
        </nav>
      );
    }

    // Bileti yoksa: her yerde "Reserve your spot" CTA
    else if (mode !== "payment" && mode !== "tickets") {
      return (
        <div className="mle-cta" ref={ctaRef}>
          {participantCount != null &&
            <div className="mle-cta-left">
              <div className="mle-cta-title">{getTranslation("buyTicket")}:</div>
              <div className="mle-cta-sub">
                {participantCount} {getTranslation('participant')}
              </div>
            </div>
          }

          <button
            className="mle-btn mle-btn-primary"
            style={{ marginLeft: 'auto' }}
            onClick={handleReserve}
          >
            {getTranslation("reserveYourSpot")}
          </button>
        </div>
      );
    }
  };
  const speakers = Array.isArray(event.speakers) ? event.speakers : [];
  const sponsors = Array.isArray(event.sponsors) ? event.sponsors : [];

  const sponsorTickets = Array.isArray(event.tickets)
    ? event.tickets.filter(t => String(t.type || '').toLowerCase() === 'sponsor')
    : [];

  const descriptionPreview = useMemo(() => {
    const text = event?.description || '';
    if (!text) return '';
    if (text.length <= 600 || showFullDesc) return text;
    return text.slice(0, 600) + '…';
  }, [event?.description, showFullDesc]);

  // ---- token (public for login/register)
  useEffect(() => {
    let mounted = true;
    Services.GetToken()
      .then((res) => {
        if (!mounted) return;
        if (res?.data?.status && res?.data?.result?.token) setApiToken(res.data.result.token);
      })
      .catch(() => { });
    return () => { mounted = false; };
  }, []);

  // ---- mevcut oturum kontrol
  const readAuthFromStorage = () => {
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const localToken = localStorage.getItem('token');
    const sessionToken = sessionStorage.getItem('token');
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    const token = (rememberMe && localToken) ? localToken : (sessionToken || null);

    if (token && userStr) {
      try {
        const parsedUser = JSON.parse(userStr);
        setIsAuth(true);
        setAuthUser(parsedUser);
      } catch {
        setIsAuth(false);
        setAuthUser(null);
      }
    } else {
      setIsAuth(false);
      setAuthUser(null);
    }
  };

  useEffect(() => { readAuthFromStorage(); }, []);

  useEffect(() => {
    if (!isAuth && showMyActivities) {
      setShowMyActivities(false);
      setMode("details");
      setActiveTab('home');
    }
  }, [isAuth, showMyActivities, setMode]);
  // ---- LOGOUT
  const handleLogout = () => {
    try {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.setItem('rememberMe', 'false');
      setIsAuth(false);
      setAuthUser(null);
      setAccountOpen(false);
      setMode("details")
      setShowHome(false)
      setShowChannelDetails(false)
      setShowNotifications(false)
      setShowMyActivities(false);
      setShowSponsorTickets(false);
      setActiveTab('home');
    } catch { }
  };

  const handleSponsorClick = async (ticket) => {
    // Giriş yoksa önce register/login aç
    if (!isAuth) {
      setRegisterOpen(true);
      return;
    }

    // Kanala katılmayı dene
    if (event?.channelID) {
      try {
        await ensureInChannel(toId(event.channelID));
      } catch { /* no-op */ }
    }

    const priceNum = Number(ticket.price) || 0;

    const ticketInfo = {
      channelID: ticket.channelID,
      eventID: ticket.eventID,
      ticketID: ticket.ticketID,
      ticketPrice: priceNum,
      name: authUser?.name || '',
      lastname: authUser?.lastname || '',
      email: authUser?.email || '',
      phone: authUser?.phone || '',
    };

    const ticketInfosMap = {
      [ticket.ticketID]: [ticketInfo],
    };

    // ÜCRETSİZ sponsor bileti ise direkt oluştur
    if (priceNum <= 0) {
      const token = getSessionToken();
      if (!token) {
        setLoginOpen(true);
        return;
      }

      try {
        await Services.SocialHiveCreateSoldTicket(
          token,
          ticket.channelID,
          ticket.eventID,
          ticket.ticketID,
          "free",
          ticketInfo.name,
          ticketInfo.lastname,
          ticketInfo.email,
          ticketInfo.phone,
          true,
          {}
        );

        setSuccessSummary({ total: 0, count: 1 });
        setSuccessOpen(true);
        setShowSponsorTickets(false);
        setMode('details');
        setParticipantCount((prev) => {
          const base = Number.isFinite(prev) ? prev : 0;
          return base + 1;
        });
      } catch (e) {
        console.error("Free sponsor ticket creation failed:", e);
      }
      return;
    }

    // Ücretli sponsor bileti → ödeme sayfasına git
    const subTotal = priceNum;
    setPaymentOrigin('sponsor');

    setPaymentData({ subTotal, ticketInfosMap });
    setShowSponsorTickets(false);
    setMode('payment');
  };

  // ---- LOGIN
  const handleDoLogin = async () => {
    if (!apiToken) return;
    setLoginLoading(true);
    setLoginError('');
    try {
      const resp = await Services.UserLogin(apiToken, loginEmail.trim(), loginPassword);
      const ok = resp?.data?.status;
      const data = resp?.data?.result;
      if (!ok || !data?.token) {
        setLoginError(getTranslation('invalidCredentials') || 'Invalid credentials');
        setLoginLoading(false);
        return;
      }

      if (loginRemember) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data));
        localStorage.setItem('rememberMe', 'true');
      } else {
        sessionStorage.setItem('token', data.token);
        sessionStorage.setItem('user', JSON.stringify(data));
        localStorage.setItem('rememberMe', 'false');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }

      try {
        broadcastAuthMessage(
          { type: 'LOGIN', token: data.token, user: data, rememberMe: loginRemember, suppressRedirect: true },
          { onlyOthers: true }
        );
      } catch { /* no-op */ }

      setIsAuth(true);
      setAuthUser(data);
      setLoginOpen(false);

      // ⬇️ BURADAN SONRASI: loginIntent kontrolü
      const intent = loginIntent;
      setLoginIntent(null);

      // Eğer login "join channel" için açıldıysa:
      if (intent?.type === 'join-channel') {
        const targetChannelID = intent.channelID || event?.channelID;
        if (targetChannelID) {
          try {
            await ensureInChannel(toId(targetChannelID));
          } catch { /* no-op */ }
        }
        // ❗ ÖNEMLİ: Tickets moduna geçme, sayfada kal
        return;
      }

      // Normal login akışı (örneğin reserve your spot → tickets)
      try {
        if (event?.channelID) await ensureInChannel(toId(event.channelID));
      } catch { /* no-op */ }

      setMode("tickets");
    } catch {
      setLoginError(getTranslation('somethingWentWrong') || 'Something went wrong');
    } finally {
      setLoginLoading(false);
    }
  };
  // ---- REGISTER (+ auto-login)
  const handleDoRegister = async () => {
    if (!apiToken) return;
    setRegLoading(true);
    setRegError('');
    try {
      const r = await Services.UserRegister(apiToken, regName.trim(), regLastname.trim(), regEmail.trim(), regPassword);
      if (!(r?.data?.status)) {
        const msg = Array.isArray(r?.data?.message) ? r.data.message.join(', ') : (r?.data?.message || '');
        if (msg.toLowerCase().includes('email already exists')) setRegError(getTranslation('emailExist') || 'Email already exists');
        else setRegError(getTranslation('invalidCredentials') || 'Could not register');
        setRegLoading(false);
        return;
      }

      const l = await Services.UserLogin(apiToken, regEmail.trim(), regPassword);
      const ok = l?.data?.status;
      const data = l?.data?.result;
      if (!ok || !data?.token) {
        setRegError(getTranslation('somethingWentWrong') || 'Login failed after register');
        setRegLoading(false);
        return;
      }

      sessionStorage.setItem('token', data.token);
      sessionStorage.setItem('user', JSON.stringify(data));
      localStorage.setItem('rememberMe', 'false');
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      try {
        broadcastAuthMessage(
          { type: 'LOGIN', token: data.token, user: data, rememberMe: false, suppressRedirect: true },
          { onlyOthers: true }
        );
      } catch { /* no-op */ }

      setIsAuth(true);
      setAuthUser(data);
      setRegisterOpen(false);

      const intent = loginIntent;
      setLoginIntent(null);

      if (intent?.type === 'join-channel') {
        const targetChannelID = intent.channelID || event?.channelID;
        if (targetChannelID) {
          try {
            await ensureInChannel(toId(targetChannelID));
          } catch { /* no-op */ }
        }
        return;
      }

      try {
        if (event?.channelID) await ensureInChannel(toId(event.channelID));
      } catch { /* no-op */ }

      setMode("tickets");
    } catch {
      setRegError(getTranslation('somethingWentWrong') || 'Something went wrong');
    } finally {
      setRegLoading(false);
    }
  };

  const joinChannelDirect = async (targetId) => {
    try {
      const token = getSessionToken();
      const uid = getCurrentUserId();
      if (!token || !uid || !targetId) return false;

      const resp = await Services.SocialHiveJoinToChannel(token, uid, targetId);
      const ok = resp?.data?.status === true;
      const msg = (resp?.data?.message || '').toLowerCase();

      // API "already" diyorsa da başarı kabul et
      if (ok || msg.includes('already')) return true;

      return false;
    } catch {
      return false;
    }
  };

  const ensureInChannel = async (targetId) => {
    if (!targetId) return;

    reserveFlowRef.current.started = true;
    reserveFlowRef.current.targetChannelID = targetId;
    reserveFlowRef.current.switchedOnce = false;

    const list = Array.isArray(myChannels) ? myChannels : [];
    const ids = list
      .map((ch) => toId(ch?.channelID ?? ch?.id ?? null))
      .filter(Boolean);

    // Zaten kanaldaysa çık
    if (ids.includes(targetId)) return;

    // 2.a) Önce doğrudan servis ile katılmayı dene (token + userID garantili)
    const joined = await joinChannelDirect(targetId);

    // 2.b) Eğer başarısızsa, son çare olarak mevcut provider fonksiyonunu dene
    if (!joined) {
      try { handleJoinChannel?.(targetId); } catch { /* no-op */ }
    }
  };

  // Katılım tamamlanınca tek sefer kanala geç
  useEffect(() => {
    const { started, targetChannelID, switchedOnce } = reserveFlowRef.current;
    if (!started || !targetChannelID || switchedOnce) return;
    const list = Array.isArray(myChannels) ? myChannels : [];
    const newChannel = list.find(
      (x) => toId(x?.channelID) === targetChannelID || toId(x?.id) === targetChannelID
    );
    if (newChannel) {
      try { handleChannelChange?.(newChannel, true); } catch { }
      reserveFlowRef.current.switchedOnce = true;
    }
  }, [myChannels, handleChannelChange]);

  const handleReserve = () => {
    if (!isAuth) { setRegisterOpen(true); return; }
    // ✅ Tickets akışına girmeden önce kanala katılmayı tetikle
    if (event?.channelID) ensureInChannel(toId(event.channelID));
    setMode('tickets');
    if (typeof onReserve === 'function') onReserve(event);
  };

  const shareUrl = `https://lodos.sh/event/${event?.eventID}/details`;

  const handleShareTo = (target) => {
    const url = encodeURIComponent(shareUrl);
    const text = encodeURIComponent(event?.title || '');

    if (target === 'instagram') {
      // Instagram için direkt web paylaşım yok; linki kopyalayıp Instagram'ı açıyoruz
      try {
        navigator.clipboard?.writeText(shareUrl);
      } catch { }
      window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
      return;
    }

    if (target === 'x') {
      const shareLink = `https://twitter.com/intent/tweet?url=${url}&text=${text}`;
      window.open(shareLink, '_blank', 'noopener,noreferrer');
      return;
    }

    if (target === 'linkedin') {
      const shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
      window.open(shareLink, '_blank', 'noopener,noreferrer');
      return;
    }

    if (target === 'whatsapp') {
      const shareLink = `https://api.whatsapp.com/send?text=${text}%20${url}`;
      window.open(shareLink, '_blank', 'noopener,noreferrer');
      return;
    }

    if (target === 'telegram') {
      const shareLink = `https://t.me/share/url?url=${url}&text=${text}`;
      window.open(shareLink, '_blank', 'noopener,noreferrer');
      return;
    }
  };

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const tmp = document.createElement('textarea');
        tmp.value = shareUrl;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
      }
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const handleGoToEvent = () => {
    // Online etkinlik: eventLink'i yeni sekmede aç
    if (event?.isOnline === "1" && event?.eventLink) {
      window.open(event.eventLink, "_blank", "noopener,noreferrer");
      return;
    }

    // Fiziksel etkinlik: adresi Google Maps'te aç
    const address = event?.locationAddress;
    if (!address) return;

    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(mapsUrl, "_blank", "noopener,noreferrer");
  };

  // Dil seçenekleri (kısa etiket)
  const LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English', short: 'EN' },
    { value: 'tr', label: 'Türkçe', short: 'TR' },
    { value: 'de', label: 'Deutsch', short: 'DE' },
    { value: 'it', label: 'Italiano', short: 'IT' },
    { value: 'es', label: 'Español', short: 'ES' },
    { value: 'fr', label: 'Français', short: 'FR' },
  ];
  const currentLang = LANGUAGE_OPTIONS.find(l => l.value === language) || LANGUAGE_OPTIONS[0];

  function isiOS() {
    if (typeof navigator === "undefined") return false;

    const ua = navigator.userAgent || navigator.vendor || window.opera;

    // iPhone, iPad, iPod için klasik kontrol
    if (/iPad|iPhone|iPod/.test(ua)) return true;

    // iPadOS 13+ kendini Mac gibi tanıttığı için ek kontrol
    const platform = navigator.platform || "";
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    if (/MacIntel/.test(platform) && maxTouchPoints > 1) {
      return true;
    }

    return false;
  }

  const isAndroid = () => {
    if (typeof navigator === "undefined") return false; // SSR / güvenlik
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    return /android/i.test(ua);
  }

  if (!event) return <div className="mle-page"><div className="mle-skeleton" /></div>;

  if (mode === 'tickets') {
    return (
      <div className="mle-page" ref={pageRef}>
        <header className="mle-topbar" style={{ marginTop: isiOS() ? 0 : 0 }}>
          <div className="mle-brand">
            <span onClick={() => {
              setMode("details");
              setShowHome(true);
              setShowNotifications(false);
              setShowChannelDetails(false);
              setActiveTab('home');
              setShowMyActivities(false);
              setShowSponsorTickets(false);
            }} className="mle-brand-text"><img src={logo} width={30} alt="logo" /></span>
          </div>

          {isAuth ? (
            <button
              className="mle-user-chip mle-user-chip-btn"
              aria-label="Profile"
              onClick={() => setAccountOpen(true)}
            >
              <img
                className="mle-user-avatar"
                src={authUser?.userAvatar || `https://ui-avatars.com/api/?name=${(authUser?.name || 'U')[0]}`}
                alt="avatar"
              />
              <span className="mle-user-name">
                {(authUser?.name || '').trim()} {(authUser?.lastname || '').trim()}
              </span>
            </button>
          ) : (
            <button className="mle-btn mle-btn-primary" onClick={() => setRegisterOpen(true)}>
              {getTranslation('signup') || 'Kayıt Ol'}
            </button>
          )}
        </header>
        <MobileTicketsPage
          event={event}
          onBack={() => setMode('details')}
          onProceedPayment={async (subTotal, ticketInfosMap) => {
            // Ücretli bilet var mı?
            const allTickets = Object.values(ticketInfosMap).flat();
            const hasPaidTicket = allTickets.some(info => Number(info.ticketPrice) > 0);
            if (isAuth && event?.channelID) {
              try { await ensureInChannel(toId(event.channelID)); } catch { /* no-op */ }
            }

            if (!hasPaidTicket) {
              // ✅ Ücretsiz tüm biletler → API ile bilet oluştur
              const token = getSessionToken();
              if (!token) {
                setLoginOpen(true);
                return;
              }

              try {
                // ÜCRETSİZ: her katılımcı için tek tek create
                // Billing boş obje gönderiyoruz (desktop akışla uyumlu)
                for (const t of allTickets) {
                  let res = await Services.SocialHiveCreateSoldTicket(
                    token,
                    t.channelID,
                    t.eventID,
                    t.ticketID,
                    "free",
                    t.name,
                    t.lastname,
                    t.email,
                    t.phone,
                    true,
                    {}
                  );

                }
                const totalAttendee = allTickets.length;
                setSuccessSummary({ total: subTotal || 0, count: totalAttendee });
                setSuccessOpen(true);
                setParticipantCount((prev) => {
                  const base = Number.isFinite(prev) ? prev : 0;
                  return base + totalAttendee;
                });
              } catch (e) {
                // no toaster on mobile view ⇒ sessiz fail yerine login'e döndürelim
                console.error("Free ticket creation failed:", e);
              }
              return;
            }

            // 💳 En az 1 ücretli → ödeme sayfasına geç
            setPaymentData({ subTotal, ticketInfosMap });
            setPaymentOrigin('tickets');
            setMode('payment');
          }}
        />
        {renderFooter()}

        {/* ACCOUNT SHEET (Dil & Logout) */}
        <MleSheet
          isOpen={accountOpen}
          onClose={() => setAccountOpen(false)}
          title={getTranslation('account') || 'Account'}
        >
          <div className="mle-account-header">
            <img
              className="mle-account-avatar"
              src={authUser?.userAvatar || `https://ui-avatars.com/api/?name=${(authUser?.name || 'U')[0]}`}
              alt="avatar"
            />
            <div className="mle-account-id">
              <div className="mle-account-name">{`${authUser?.name || ''} ${authUser?.lastname || ''}`.trim()}</div>
              {!!authUser?.email && (
                <div className="mle-account-email">
                  <TbMail /> <span>{authUser.email}</span>
                </div>
              )}
            </div>
          </div>

          <div className="mle-list">
            <div className="mle-list-item">
              <div className="mle-list-left">
                <div className="mle-list-ico"><TbWorld /></div>
                <div className="mle-list-texts">
                  <div className="mle-list-title">{getTranslation('language') || 'Language'}</div>
                  <div className="mle-list-sub">{getTranslation('changeLanguage') || 'Change application language'}</div>
                </div>
              </div>

              <div className="mle-list-right">
                <button
                  type="button"
                  className="mle-lang-btn"
                  onClick={() => setLangSheetOpen(true)}
                  aria-haspopup="dialog"
                  aria-expanded={langSheetOpen}
                  aria-label="Change language"
                >
                  <span className="mle-lang-badge">{currentLang.short}</span>
                  <TbChevronDown className="mle-lang-caret" />
                </button>
              </div>
            </div>

            <button type="button" className="mle-list-item mle-danger" onClick={handleLogout}>
              <div className="mle-list-left">
                <div className="mle-list-ico"><TbLogout /></div>
                <div className="mle-list-texts">
                  <div className="mle-list-title">{getTranslation('logout') || 'Log out'}</div>
                  <div className="mle-list-sub">{getTranslation('logoutFromThisDevice') || 'Sign out from this device'}</div>
                </div>
              </div>
            </button>
          </div>
        </MleSheet>

        {/* LANGUAGE SHEET */}
        <MleSheet
          isOpen={langSheetOpen}
          onClose={() => setLangSheetOpen(false)}
          title={getTranslation('language') || 'Language'}
        >
          <div className="mle-lang-list">
            {[
              { value: 'en', label: 'English', short: 'EN' },
              { value: 'tr', label: 'Türkçe', short: 'TR' },
              { value: 'de', label: 'Deutsch', short: 'DE' },
              { value: 'it', label: 'Italiano', short: 'IT' },
              { value: 'es', label: 'Español', short: 'ES' },
              { value: 'fr', label: 'Français', short: 'FR' },
            ].map(opt => {
              const selected = opt.value === language;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`mle-lang-row ${selected ? 'is-selected' : ''}`}
                  onClick={() => {
                    changeLanguage(opt.value);
                    setLangSheetOpen(false);
                  }}
                >
                  <div className="mle-lang-left">
                    <span className="mle-lang-badge">{opt.short}</span>
                    <span className="mle-lang-label">{opt.label}</span>
                  </div>
                  {selected && <TbCircleCheck className="mle-lang-check" aria-hidden />}
                </button>
              );
            })}
          </div>
        </MleSheet>

        <MleSheet
          isOpen={successOpen}
          onClose={() => {
            setSuccessOpen(false);
            setMode('details');
          }}
          title={getTranslation('success') || 'Başarılı'}
        >
          <div className="mle-success">
            <div className="mle-success-ico">
              <TbCircleCheck aria-hidden />
            </div>

            <h3 className="mle-success-title">
              {getTranslation('ticketPurchased') || 'Biletin Hazır!'}
            </h3>

            {successSummary.total === 0 &&
              <p className="mle-success-sub">
                {getTranslation('freeTicketsIssued') || 'Ücretsiz bilet(ler)in oluşturuldu.'}
              </p>
            }

            <div className="mle-success-info">
              <div className="mle-success-row">
                <span>{getTranslation('event') || 'Etkinlik'}</span>
                <strong>{event?.title}</strong>
              </div>
              <div className="mle-success-row">
                <span>{getTranslation('attendeeCount') || 'Katılımcı'}</span>
                <strong>{successSummary.count}</strong>
              </div>
              <div className="mle-success-row">
                <span>{getTranslation('total') || 'Toplam'}</span>
                <strong>{successSummary.total} {event?.tickets?.[0]?.currency || 'TRY'}</strong>
              </div>
            </div>

            <div className="mle-success-note">
              {getTranslation('eticketMailInfo') || 'E-bilet bilgileri kayıtlı e-postana iletildi.'}
            </div>

            <div className="mle-success-actions">
              <button
                className="mle-btn mle-btn-primary mle-btn-block mle-btn-lg"
                onClick={() => {
                  setSuccessOpen(false);
                  setMode('details');
                }}
              >
                {getTranslation('close') || 'Kapat'}
              </button>
            </div>
          </div>
        </MleSheet>
      </div >
    );
  }

  if (mode === 'payment') {

    return (
      <div className="mle-page" ref={pageRef}>
        <header className="mle-topbar" style={{ marginTop: isiOS() ? 0 : 0 }}>
          <div className="mle-brand">
            <span onClick={() => {
              setMode("details");
              setShowHome(true);
              setShowNotifications(false);
              setShowChannelDetails(false);
              setActiveTab('home');
              setShowMyActivities(false);
              setShowSponsorTickets(false);
            }} className="mle-brand-text"><img src={logo} width={30} alt="logo" /></span>
          </div>

          {isAuth ? (
            <button
              className="mle-user-chip mle-user-chip-btn"
              aria-label="Profile"
              onClick={() => setAccountOpen(true)}
            >
              <img
                className="mle-user-avatar"
                src={authUser?.userAvatar || `https://ui-avatars.com/api/?name=${(authUser?.name || 'U')[0]}`}
                alt="avatar"
              />
              <span className="mle-user-name">
                {(authUser?.name || '').trim()} {(authUser?.lastname || '').trim()}
              </span>
            </button>
          ) : (
            <button className="mle-btn mle-btn-primary" onClick={() => setRegisterOpen(true)}>
              {getTranslation('signup') || 'Kayıt Ol'}
            </button>
          )}
        </header>
        <MobilePaymentPage
          event={event}
          onBack={() => {
            if (paymentOrigin === 'sponsor') {
              // Sponsor akışından geldiyse tekrar sponsor paketlerine dön
              setMode('details');
              setShowSponsorTickets(true);
              setShowHome(false);
              setShowNotifications(false);
              setShowChannelDetails(false);
              setShowMyActivities(false);
              setActiveTab('');
            } else {
              // Normal bilet akışı
              setMode('tickets');
            }
          }} paymentData={paymentData}
          getSessionToken={getSessionToken}
          getCurrentUserId={getCurrentUserId}
          onSuccess={(summary) => {
            const total = summary?.total ?? 0;
            const count = summary?.count ?? 0;
            setSuccessSummary({ total, count });
            setSuccessOpen(true);
            setMode('tickets');
            setParticipantCount((prev) => {
              const base = Number.isFinite(prev) ? prev : 0;
              return base + count;
            });
          }}
        />
        {renderFooter()}

        {/* ACCOUNT SHEET (Dil & Logout) */}
        <MleSheet
          isOpen={accountOpen}
          onClose={() => setAccountOpen(false)}
          title={getTranslation('account') || 'Account'}
        >
          <div className="mle-account-header">
            <img
              className="mle-account-avatar"
              src={authUser?.userAvatar || `https://ui-avatars.com/api/?name=${(authUser?.name || 'U')[0]}`}
              alt="avatar"
            />
            <div className="mle-account-id">
              <div className="mle-account-name">{`${authUser?.name || ''} ${authUser?.lastname || ''}`.trim()}</div>
              {!!authUser?.email && (
                <div className="mle-account-email">
                  <TbMail /> <span>{authUser.email}</span>
                </div>
              )}
            </div>
          </div>

          <div className="mle-list">
            <div className="mle-list-item">
              <div className="mle-list-left">
                <div className="mle-list-ico"><TbWorld /></div>
                <div className="mle-list-texts">
                  <div className="mle-list-title">{getTranslation('language') || 'Language'}</div>
                  <div className="mle-list-sub">{getTranslation('changeLanguage') || 'Change application language'}</div>
                </div>
              </div>

              <div className="mle-list-right">
                <button
                  type="button"
                  className="mle-lang-btn"
                  onClick={() => setLangSheetOpen(true)}
                  aria-haspopup="dialog"
                  aria-expanded={langSheetOpen}
                  aria-label="Change language"
                >
                  <span className="mle-lang-badge">{currentLang.short}</span>
                  <TbChevronDown className="mle-lang-caret" />
                </button>
              </div>
            </div>

            <button type="button" className="mle-list-item mle-danger" onClick={handleLogout}>
              <div className="mle-list-left">
                <div className="mle-list-ico"><TbLogout /></div>
                <div className="mle-list-texts">
                  <div className="mle-list-title">{getTranslation('logout') || 'Log out'}</div>
                  <div className="mle-list-sub">{getTranslation('logoutFromThisDevice') || 'Sign out from this device'}</div>
                </div>
              </div>
            </button>
          </div>
        </MleSheet>

        {/* LANGUAGE SHEET */}
        <MleSheet
          isOpen={langSheetOpen}
          onClose={() => setLangSheetOpen(false)}
          title={getTranslation('language') || 'Language'}
        >
          <div className="mle-lang-list">
            {[
              { value: 'en', label: 'English', short: 'EN' },
              { value: 'tr', label: 'Türkçe', short: 'TR' },
              { value: 'de', label: 'Deutsch', short: 'DE' },
              { value: 'it', label: 'Italiano', short: 'IT' },
              { value: 'es', label: 'Español', short: 'ES' },
              { value: 'fr', label: 'Français', short: 'FR' },
            ].map(opt => {
              const selected = opt.value === language;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`mle-lang-row ${selected ? 'is-selected' : ''}`}
                  onClick={() => {
                    changeLanguage(opt.value);
                    setLangSheetOpen(false);
                  }}
                >
                  <div className="mle-lang-left">
                    <span className="mle-lang-badge">{opt.short}</span>
                    <span className="mle-lang-label">{opt.label}</span>
                  </div>
                  {selected && <TbCircleCheck className="mle-lang-check" aria-hidden />}
                </button>
              );
            })}
          </div>
        </MleSheet>

        <MleSheet
          isOpen={successOpen}
          onClose={() => {
            setSuccessOpen(false);
            setMode('details');
          }}
          title={getTranslation('success') || 'Başarılı'}
        >
          <div className="mle-success">
            <div className="mle-success-ico">
              <TbCircleCheck aria-hidden />
            </div>

            <h3 className="mle-success-title">
              {getTranslation('ticketPurchased') || 'Biletin Hazır!'}
            </h3>
            <div className="mle-success-info">
              <div className="mle-success-row">
                <span>{getTranslation('event') || 'Etkinlik'}</span>
                <strong>{event?.title}</strong>
              </div>
              <div className="mle-success-row">
                <span>{getTranslation('attendeeCount') || 'Katılımcı'}</span>
                <strong>{successSummary.count}</strong>
              </div>
              <div className="mle-success-row">
                <span>{getTranslation('total') || 'Toplam'}</span>
                <strong>{successSummary.total} {event?.tickets?.[0]?.currency || 'TRY'}</strong>
              </div>
            </div>

            <div className="mle-success-note">
              {getTranslation('eticketMailInfo') || 'E-bilet bilgileri kayıtlı e-postana iletildi.'}
            </div>

            <div className="mle-success-actions">
              <button
                className="mle-btn mle-btn-primary mle-btn-block mle-btn-lg"
                onClick={() => {
                  setSuccessOpen(false);
                  setMode('details');
                }}
              >
                {getTranslation('close') || 'Kapat'}
              </button>
            </div>
          </div>
        </MleSheet>
      </div>
    );
  }

  return (
    <div className="mle-page" ref={pageRef}>
      <header className="mle-topbar" style={{ marginTop: isiOS() ? 0 : 0 }}>
        <div className="mle-brand">
          <span onClick={() => {
            setMode("details");
            setShowHome(true);
            setShowNotifications(false);
            setShowChannelDetails(false);
            setActiveTab('home');
            setShowMyActivities(false);
            setShowSponsorTickets(false);
          }} className="mle-brand-text"><img src={logo} width={30} alt="logo" /></span>
        </div>

        {isAuth ? (
          <button
            className="mle-user-chip mle-user-chip-btn"
            aria-label="Profile"
            onClick={() => setAccountOpen(true)}
          >
            <img
              className="mle-user-avatar"
              src={authUser?.userAvatar || `https://ui-avatars.com/api/?name=${(authUser?.name || 'U')[0]}`}
              alt="avatar"
            />
            <span className="mle-user-name">
              {(authUser?.name || '').trim()} {(authUser?.lastname || '').trim()}
            </span>
          </button>
        ) : (
          <button className="mle-btn mle-btn-primary" onClick={() => setRegisterOpen(true)}>
            {getTranslation('signup') || 'Kayıt Ol'}
          </button>
        )}
      </header>

      {showChannelDetails ? (
        <div className="mle-channel-page">
          <MobileChannelDetails
            channelID={event?.channelID}
            token={apiToken}
            onBack={() => {
              setShowChannelDetails(false);
              setMode("details");
            }}
            onRequestLogin={() => {
              setLoginIntent({ type: "join-channel", channelID: event?.channelID });
              setLoginOpen(true);
            }}
          />
        </div>
      ) : showNotifications ? (
        <div className="mle-notifications-page">
          <MobileEventsNotification
            token={getSessionToken()}
            setShowNotifications={setShowNotifications}
          />
        </div>
      ) : showHome ? (
        <MobileEventsHome token={apiToken} setShowHome={setShowHome} />
      ) : showMyActivities ? (
        <MobileMyActivitiesPage
          onBack={() => {
            setShowMyActivities(false);
            setMode("details");
            setActiveTab('home');
          }}
        />
      ) : showSponsorTickets ? (
        // 🔹 SPONSOR TICKET SAYFASI
        <div className="mle-body">
          {/* Geri butonu */}
          <button
            type="button"
            className="mle-activities-back"
            style={{ marginBottom: 12 }}
            onClick={() => {
              setShowSponsorTickets(false);
              setMode('details');
            }}
          >
            <TbArrowLeft size={18} />
            <span>{getTranslation("back") || "Back"}</span>
          </button>

          <h1 className="mle-main-title">
            {getTranslation('becomeASponsor') || 'Become a Sponsor'}
          </h1>

          <div className="mle-sponsor-tickets-list">
            {sponsorTickets.map((t) => {
              const priceNum = Number(t.price) || 0;
              const priceLabel =
                priceNum > 0
                  ? `${priceNum} ${t.currency || 'TRY'}`
                  : (getTranslation('free') || 'Free');

              return (
                <div
                  key={t.ticketID || t.id}
                  className="mle-sponsor-ticket-card"
                >
                  <div className="mle-sponsor-ticket-header">
                    <div className="mle-sponsor-ticket-title">{t.title}</div>
                    {t.type && (
                      <div className="mle-sponsor-ticket-tag">
                        {t.type}
                      </div>
                    )}
                  </div>

                  {t.description && (
                    <p className="mle-sponsor-ticket-desc">
                      {t.description}
                    </p>
                  )}

                  <div className="mle-sponsor-ticket-footer">
                    <button
                      type="button"
                      className="mle-btn mle-btn-primary mle-sponsor-ticket-btn"
                      onClick={() => handleSponsorClick(t)}
                    >
                      {priceLabel}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) :
        (
          <>
            <div className="mle-cover-wrapper">
              {event?.cover && <img className="mle-cover-img" src={event.cover} alt={event?.title} />}
            </div>

            <div ref={contentRef} className="mle-body">
              <h1 className="mle-main-title">{event?.title}</h1>

              {event?.channelDetails?.title && (
                <div className="mle-channel-row">
                  {event.channelDetails.logo && (
                    <img
                      className="mle-channel-logo"
                      src={event.channelDetails.logo}
                      alt={event.channelDetails.title}
                    />
                  )}
                  <span
                    className="mle-channel-name"
                    style={{ alignItems: 'center', justifyContent: 'center', display: 'flex' }}
                    onClick={() => {
                      setMode("details");
                      setShowHome(false);
                      setShowNotifications(false);
                      setShowSponsorTickets(false);
                      setShowChannelDetails(true);
                      setActiveTab('');
                    }}
                  >
                    {event.channelDetails.title} <TbChevronRight style={{ marginLeft: '5px' }} />
                  </span>
                </div>
              )}

              <div className="mle-info-separator">
                <div className="mle-info-item-full">
                  <div className="mle-info-ico"><TbCalendarEvent size={24} /></div>
                  <div className="mle-info-texts">
                    <div className="mle-info-title">{dateText}</div>
                    <div className="mle-info-sub">{timeText}</div>
                  </div>
                </div>
              </div>

              <div className="mle-info-separator">
                <div className="mle-info-item-full">
                  <div className="mle-info-ico"><TbMapPin size={24} /></div>
                  <div className="mle-info-texts">
                    <div className="mle-info-title">{placeTitle}</div>
                    <div className="mle-info-sub">{locationText}</div>
                  </div>
                </div>
              </div>

              {userHasTicket && (
                <div className="mle-owned-box">
                  <div className="mle-owned-header">
                    <div className="mle-owned-texts">
                      <div className="mle-owned-title">
                        {getTranslation('ticketReservedTitle') || 'Harika haber, etkinlik için yerin ayrıldı!'}
                      </div>
                      {participantCount != null && (
                        <div className="mle-owned-sub">
                          {(getTranslation('ticketReservedSubtitle', { participantCount }))}
                        </div>
                      )}
                    </div>

                    <label
                      className="mle-owned-check"
                      onClick={() => setShareSheetOpen(true)}
                      aria-label={getTranslation('shareEvent') || 'Etkinliği paylaş'}
                    >
                      <span className="mle-owned-check-box">
                        <TbShare color='#8B949E' size={14} />
                      </span>
                    </label>
                  </div>

                  <div className="mle-owned-actions">
                    <button
                      type="button"
                      className="mle-btn mle-btn-primary mle-owned-btn"
                      style={{ fontSize: '12px' }}
                      onClick={handleGoToEvent}
                    >
                      {getTranslation('goToEvent') || 'Etkinliğe Git'}
                    </button>

                    {Array.isArray(event?.tickets) && event.tickets.length > 0 && (
                      <button
                        style={{ fontSize: '12px' }}
                        type="button"
                        className="mle-btn mle-btn-ghost mle-owned-btn"
                        onClick={handleReserve}
                      >
                        {getTranslation('buyMoreTickets') || 'Daha Fazla Bilet Al'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!!event?.description && (
                <section className="mle-section">
                  <h2 className="mle-section-title">{getTranslation("aboutEvent")}:</h2>
                  <div className="mle-desc-block">
                    <p className="mle-desc-content">
                      {descriptionPreview}
                      {event?.description?.length > 600 && (
                        <button type="button" className="mle-link" onClick={() => setShowFullDesc(s => !s)}>
                          {showFullDesc ? (getTranslation('showLess') || 'Daha Az Göster') : (getTranslation('showMore') || 'Devamını Gör')}
                        </button>
                      )}
                    </p>
                  </div>
                </section>
              )}

              <section className="mle-section">
                <h2 className="mle-section-title">{getTranslation("speakers")}</h2>
                <div className="mle-speaker-grid">
                  {speakers.length > 0 ? (
                  speakers.map((sp, i) => {
                    const linkedin = sp?.socialMediaLinks?.linkedin || sp?.linkedin;
                      const topic = sp?.speakingAboutTitle === "-" ? sp?.about : (sp?.speakingAboutTitle || sp?.about);
                    return (
                      <div
                        className="mle-speaker-card"
                        key={`speaker-${i}`}
                        role={linkedin ? "link" : "article"}
                        aria-disabled={!linkedin}
                        style={{ cursor: linkedin ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (!linkedin) return;
                          window.open(linkedin, "_blank", "noopener,noreferrer");
                        }}
                      >
                        <img className="mle-speaker-avatar" src={sp.image} alt={`${sp.name} ${sp.lastname}`} />
                        <div className="mle-speaker-content">
                          <div className="mle-speaker-name">{`${sp.name} ${sp.lastname}`.trim()}</div>
                            <div className="mle-speaker-title">{topic}</div>
                        </div>
                      </div>
                    )
                  })
                  ) : (
                    <div className="mle-empty">{getTranslation("noSpeakersYet")}</div>
                  )}
                </div>
              </section>

              <section className="mle-section" style={{ marginBottom: '135px' }}>
                <h2 className="mle-section-title">{getTranslation("sponsors")}</h2>
                <div className="mle-sponsor-grid">
                  {sponsors.length > 0 && sponsors.map((sp, i) => (
                    <div className="mle-sponsor-box" key={`sponsor-${i}`}>
                      {sp.image ? (
                        <img className="mle-sponsor-logo" src={sp.image} alt={sp.companyTitle} />
                      ) : (
                        <div className="mle-sponsor-placeholder" />
                      )}
                      <div className="mle-sponsor-title">{sp.companyTitle}</div>
                      {sp.sponsorshipType && (
                        <div className="mle-sponsor-type">{sp.sponsorshipType}</div>
                      )}
                    </div>
                  ))}

                  {/* 🔹 En az 1 sponsor tipi bilet varsa: Become a Sponsor kutusu */}
                  {sponsorTickets.length > 0 && (
                    <button
                      type="button"
                      className="mle-sponsor-box mle-sponsor-cta-box"
                      onClick={() => {
                        setShowSponsorTickets(true);
                        setShowHome(false);
                        setShowNotifications(false);
                        setShowChannelDetails(false);
                        setShowMyActivities(false);
                        setActiveTab('');
                        setMode('details');
                      }}
                    >
                      <div className="mle-sponsor-title">
                        {getTranslation('becomeASponsor') || 'Become a Sponsor'}
                      </div>

                      <div className="mle-sponsor-type">
                        {getTranslation('seeSponsorPackages') || 'See sponsor packages'}
                      </div>
                    </button>
                  )}
                </div>
              </section>
            </div>


          </>
        )
      }
      {renderFooter()}


      {/* ACCOUNT SHEET */}
      <MleSheet
        isOpen={accountOpen}
        onClose={() => setAccountOpen(false)}
        title={getTranslation('account') || 'Account'}
      >
        <div className="mle-account-header">
          <img
            className="mle-account-avatar"
            src={authUser?.userAvatar || `https://ui-avatars.com/api/?name=${(authUser?.name || 'U')[0]}`}
            alt="avatar"
          />
          <div className="mle-account-id">
            <div className="mle-account-name">{`${authUser?.name || ''} ${authUser?.lastname || ''}`.trim()}</div>
            {!!authUser?.email && (
              <div className="mle-account-email">
                <TbMail /> <span>{authUser.email}</span>
              </div>
            )}
          </div>
        </div>

        <div className="mle-list">
          <div className="mle-list-item">
            <div className="mle-list-left">
              <div className="mle-list-ico"><TbWorld /></div>
              <div className="mle-list-texts">
                <div className="mle-list-title">{getTranslation('language') || 'Language'}</div>
                <div className="mle-list-sub">{getTranslation('changeLanguage') || 'Change application language'}</div>
              </div>
            </div>

            <div className="mle-list-right">
              <button
                type="button"
                className="mle-lang-btn"
                onClick={() => setLangSheetOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={langSheetOpen}
                aria-label="Change language"
              >
                <span className="mle-lang-badge">{currentLang.short}</span>
                <TbChevronDown className="mle-lang-caret" />
              </button>
            </div>
          </div>

          <button type="button" className="mle-list-item mle-danger" onClick={handleLogout}>
            <div className="mle-list-left">
              <div className="mle-list-ico"><TbLogout /></div>
              <div className="mle-list-texts">
                <div className="mle-list-title">{getTranslation('logout') || 'Log out'}</div>
                <div className="mle-list-sub">{getTranslation('logoutFromThisDevice') || 'Sign out from this device'}</div>
              </div>
            </div>
          </button>
        </div>
      </MleSheet>

      {/* LANGUAGE SHEET */}
      <MleSheet
        isOpen={langSheetOpen}
        onClose={() => setLangSheetOpen(false)}
        title={getTranslation('language') || 'Language'}
      >
        <div className="mle-lang-list">
          {[
            { value: 'en', label: 'English', short: 'EN' },
            { value: 'tr', label: 'Türkçe', short: 'TR' },
            { value: 'de', label: 'Deutsch', short: 'DE' },
            { value: 'it', label: 'Italiano', short: 'IT' },
            { value: 'es', label: 'Español', short: 'ES' },
            { value: 'fr', label: 'Français', short: 'FR' },
          ].map(opt => {
            const selected = opt.value === language;
            return (
              <button
                key={opt.value}
                type="button"
                className={`mle-lang-row ${selected ? 'is-selected' : ''}`}
                onClick={() => {
                  changeLanguage(opt.value);
                  setLangSheetOpen(false);
                }}
              >
                <div className="mle-lang-left">
                  <span className="mle-lang-badge">{opt.short}</span>
                  <span className="mle-lang-label">{opt.label}</span>
                </div>
                {selected && <TbCircleCheck className="mle-lang-check" aria-hidden />}
              </button>
            );
          })}
        </div>
      </MleSheet>

      {/* SHARE SHEET */}
      <MleSheet
        isOpen={shareSheetOpen}
        onClose={() => setShareSheetOpen(false)}
        title={getTranslation('shareEvent') || 'Etkinliği Paylaş'}
      >
        <div className="mle-share-list">
          <button
            type="button"
            className="mle-share-row"
            onClick={() => handleShareTo('instagram')}
          >
            <TbBrandInstagram className="mle-share-ico" />
            <span className="mle-share-label">
              {getTranslation('shareOnInstagram') || 'Etkinliği Instagram’da Paylaş'}
            </span>
          </button>

          <button
            type="button"
            className="mle-share-row"
            onClick={() => handleShareTo('x')}
          >
            <TbBrandX className="mle-share-ico" />
            <span className="mle-share-label">
              {getTranslation('shareOnX') || "Etkinliği X'de Paylaş"}
            </span>
          </button>

          <button
            type="button"
            className="mle-share-row"
            onClick={() => handleShareTo('linkedin')}
          >
            <TbBrandLinkedin className="mle-share-ico" />
            <span className="mle-share-label">
              {getTranslation('shareOnLinkedin') || 'Etkinliği Linkedin’de Paylaş'}
            </span>
          </button>

          <button
            type="button"
            className="mle-share-row"
            onClick={() => handleShareTo('whatsapp')}
          >
            <TbBrandWhatsapp className="mle-share-ico" />
            <span className="mle-share-label">
              {getTranslation('shareOnWhatsapp') || 'Etkinliği Whatsapp’da Paylaş'}
            </span>
          </button>

          <button
            type="button"
            className="mle-share-row"
            onClick={() => handleShareTo('telegram')}
          >
            <TbBrandTelegram className="mle-share-ico" />
            <span className="mle-share-label">
              {getTranslation('shareOnTelegram') || 'Etkinliği Telegram’da Paylaş'}
            </span>
          </button>
        </div>

        <div className="mle-share-copy">
          <button
            type="button"
            className="mle-btn mle-btn-primary mle-btn-block mle-btn-lg"
            onClick={handleCopyLink}
          >
            <TbLink style={{ marginRight: 6 }} />
            {getTranslation('copyEventLink') || 'Etkinlik linkini kopyala'}
          </button>
        </div>
      </MleSheet>

      {/* LOGIN SHEET */}
      <MleSheet
        isOpen={loginOpen}
        onClose={() => { if (!loginLoading) setLoginOpen(false); }}
        title={getTranslation('login') || 'Log In'}
      >
        <form className="mle-form" onSubmit={(e) => { e.preventDefault(); handleDoLogin(); }}>
          <label className="mle-label">{getTranslation('email') || 'Email'}</label>
          <input
            className="mle-input"
            type="email"
            inputMode="email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            placeholder="you@example.com"
          />

          <label className="mle-label">{getTranslation('password') || 'Password'}</label>
          <input
            className="mle-input"
            style={{ WebkitTextSecurity: 'disc' }}
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value.replace(/\s/g, ''))}
            placeholder="••••••••"
            onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
          />

          <label className="mle-checkbox">
            <input type="checkbox" checked={loginRemember} onChange={() => setLoginRemember(v => !v)} />
            <span>{getTranslation('rememberMe') || 'Remember me'}</span>
          </label>

          {loginError && <div className="mle-error">{loginError}</div>}

          <button type="submit" className="mle-btn mle-btn-primary mle-btn-block mle-btn-lg" disabled={loginLoading}>
            {loginLoading ? (getTranslation('loading') || 'Loading...') : (getTranslation('login') || 'Log In')}
          </button>

          <button
            type="button"
            className="mle-btn mle-btn-outline mle-btn-block mle-btn-lg"
            onClick={() => { if (!loginLoading) { setLoginOpen(false); setRegisterOpen(true); } }}
          >
            {getTranslation('signup') || 'Sign up'}
          </button>
        </form>
      </MleSheet>

      {/* REGISTER SHEET */}
      <MleSheet
        isOpen={registerOpen}
        onClose={() => { if (!regLoading) setRegisterOpen(false); }}
        title={getTranslation('signup') || 'Sign Up'}
      >
        <form className="mle-form" onSubmit={(e) => { e.preventDefault(); handleDoRegister(); }}>
          <label className="mle-label">{getTranslation('name') || 'Name'}</label>
          <input className="mle-input" type="text" autoComplete="given-name" value={regName} onChange={(e) => setRegName(e.target.value)} placeholder={getTranslation('name')} />

          <label className="mle-label">{getTranslation('lastname') || 'Lastname'}</label>
          <input className="mle-input" type="text" autoComplete="family-name" value={regLastname} onChange={(e) => setRegLastname(e.target.value)} placeholder={getTranslation('lastname')} />

          <label className="mle-label">{getTranslation('email') || 'Email'}</label>
          <input className="mle-input" type="email" inputMode="email" autoComplete="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} placeholder="you@example.com" />

          <label className="mle-label">{getTranslation('password') || 'Password'}</label>
          <input
            className="mle-input"
            style={{ WebkitTextSecurity: 'disc' }}
            value={regPassword}
            onChange={(e) => setRegPassword(e.target.value.replace(/\s/g, ''))}
            placeholder={getTranslation('password')}
            onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
          />

          {/* Şifre şartları */}
          <div className="mle-req-grid">

            <div className="mle-req-col">
              <p className={`mle-req ${reqLen ? 'mle-req-ok' : 'mle-req-bad'}`}>• {getTranslation("atLeast8Characters")}</p>
              <p className={`mle-req ${reqSpecial ? 'mle-req-ok' : 'mle-req-bad'}`}>• {getTranslation("atLeast1SpecialCharacter")}</p>
              <p className={`mle-req ${reqNumber ? 'mle-req-ok' : 'mle-req-bad'}`}>• {getTranslation("atLeast1Number")}</p>
            </div>

            <div className="mle-req-col">
              <p className={`mle-req ${reqUpper ? 'mle-req-ok' : 'mle-req-bad'}`}>• {getTranslation("atLeast1UppercaseLetter")}</p>
              <p className={`mle-req ${reqLower ? 'mle-req-ok' : 'mle-req-bad'}`}>• {getTranslation("atLeast1LowercaseLetter")}</p>
            </div>

          </div>

          {regError && <div className="mle-error">{regError}</div>}

          <button type="submit" className="mle-btn mle-btn-primary mle-btn-block mle-btn-lg" disabled={regLoading}>
            {regLoading ? (getTranslation('loading') || 'Loading...') : (getTranslation('signup') || 'Sign Up')}
          </button>

          <button
            type="button"
            style={{ marginBottom: '15px' }}
            className="mle-btn mle-btn-outline mle-btn-block mle-btn-lg"
            onClick={() => { if (!regLoading) { setRegisterOpen(false); setLoginOpen(true); } }}
          >
            {getTranslation('login') || 'Log in'}
          </button>
        </form>
      </MleSheet>
    </div>
  );
}